import { motion, useInView, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp } from "lucide-react";
import api from "@/lib/api";

function Counter({ to, suffix = "", prefix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  const display = to >= 1000 ? Math.floor(val).toLocaleString() : val.toFixed(to % 1 === 0 ? 0 : 1);
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{display}{suffix}
    </span>
  );
}

// Default values (used until CMS responds; same as backend defaults)
const DEFAULTS = {
  stats_traders_funded: 12480,
  stats_gains_paid_m: 8.4,
  stats_countries: 78,
  stats_payout_speed_h: 24,
};

export default function StatsCounters() {
  const { t } = useTranslation();
  const [cms, setCms] = useState(DEFAULTS);
  useEffect(() => {
    api
      .get("/content")
      .then(({ data }) => setCms({ ...DEFAULTS, ...(data || {}) }))
      .catch(() => {});
  }, []);
  const STATS = [
    { key: "tradersFunded", to: Number(cms.stats_traders_funded) || DEFAULTS.stats_traders_funded, prefix: "+", suffix: "",   gradient: true, trend: "up" },
    { key: "gainsPaid",     to: Number(cms.stats_gains_paid_m)   || DEFAULTS.stats_gains_paid_m,   prefix: "$", suffix: "M+", gradient: true, trend: "up" },
    { key: "countries",     to: Number(cms.stats_countries)      || DEFAULTS.stats_countries,      prefix: "",  suffix: "",   gradient: true, trend: "up" },
    { key: "payoutSpeed",   to: Number(cms.stats_payout_speed_h) || DEFAULTS.stats_payout_speed_h, prefix: "",  suffix: "h",  gradient: true, trend: null },
  ].map((s) => ({ ...s, label: t(`stats.${s.key}`) }));
  return (
    <section data-testid="stats-section" className="relative py-12 lg:py-16 overflow-hidden">
      {/* Removed the radial-gradient backdrop blob — section sits flush
          with the page background. */}
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="card-beam card-beam-glow py-10 px-5 lg:px-12 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(170,222,235,0.06) 0%, transparent 40%, transparent 60%, rgba(170,222,235,0.06) 100%)",
            }}
          />
          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-6 lg:gap-x-0">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.45 }}
                className="relative flex flex-col items-center justify-center text-center min-w-0 px-2"
                data-testid={`stat-${i}`}
              >
                {/* Reduced from text-6xl → text-5xl at lg so the widest
                    value ("+12,480") no longer feels visually heavier
                    than the shortest ("78"). Each value is now centred
                    inside its own divider-bounded cell. */}
                <div className={`font-display font-medium leading-none mb-2 text-3xl sm:text-4xl lg:text-5xl ${s.gradient ? "text-gradient-brand" : "text-white"}`}>
                  <Counter to={s.to} prefix={s.prefix} suffix={s.suffix} />
                </div>
                <div className="flex flex-col items-center gap-1 mt-1 w-full">
                  {s.trend === "up" && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold tracking-wider"
                      style={{
                        background: "rgba(80, 220, 130, 0.12)",
                        color: "#54d97c",
                        border: "1px solid rgba(80, 220, 130, 0.32)",
                      }}
                      data-testid={`stat-trend-${i}`}
                    >
                      <TrendingUp size={9} strokeWidth={2.6} />
                      {t("markets.live", "LIVE")}
                    </span>
                  )}
                  <div className="font-display text-[10px] sm:text-[11px] lg:text-[12px] text-zinc-400 font-medium leading-tight max-w-full px-1">{s.label}</div>
                </div>
                {i < STATS.length - 1 && (
                  <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-12 w-px bg-gradient-to-b from-transparent via-brand/30 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
